import { Stack, Box, IconButton, Spinner, type ButtonProps, For, Badge, Heading } from '@chakra-ui/react'
import type { PropsOf } from '@emotion/react'
import { Position } from '@xyflow/react'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useMemo } from 'react'
import { useState } from 'react'

import ExtractMetricsFromFormulaPanel from './panels/extractMetricsFromFormulaPanel'
import SuggestNodesPanel from './panels/suggestNodesPanel'

import { Button } from '@app/components/ui/button'
import { PopoverArrow, PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from '@app/components/ui/popover'
import useGetObject from '@app/hooks/useGetObject'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import type { DomainMetric, DomainNode, DomainObject } from '@app/types'
import IconMap from '@app/utils/iconMap'

export type DirectionType = 'upstream' | 'downstream'

interface PanelComponentProps {
  node: DomainNode
  domainObject: DomainObject
  aiAssistantSlug: string
  handle: Position
  direction: DirectionType
  handleBack: () => void
  setLoading: (state: boolean) => void
  loading: boolean
  onSuccess: () => void
}

type Props = {
  node: Pick<DomainNode, 'data'> & PropsOf<typeof SuggestNodesPanel>['node']
  position: Position
}

type Agent = {
  direction: DirectionType
  aiAssistantSlug: string
  buttonText: string
  beta: boolean
  PanelComponent: React.ComponentType<PanelComponentProps>
}

const aiButtonStyles: ButtonProps = {
  variant: 'ghost',
  w: 'full'
}
const offset = 23 * 2

const AiNodeMenu: FC<Props> = ({ node, position }) => {
  const domainObjectData = node.data
  const domainObject = useGetObject(domainObjectData.id, domainObjectData.classType) as DomainObject &
    Pick<DomainMetric, 'sourceName'>

  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [activePanel, setActivePanel] = useState<string | null>(null)
  const [hasInteracted, setHasInteracted] = useState(false)
  const { user } = useStoreCurrentUser()
  const abilities = abilitiesFor(user)
  const isAdmin = abilities.can('view', 'admin')

  const agents: Agent[] = useMemo(() => {
    const array = [
      {
        direction: 'downstream' as const,
        aiAssistantSlug: 'suggest-business-output-metrics',
        buttonText: 'Suggest business output metrics',
        beta: false,
        PanelComponent: SuggestNodesPanel
      },
      {
        direction: 'upstream' as const,
        aiAssistantSlug: 'suggest-input-metrics',
        buttonText: 'Suggest input metrics',
        beta: false,
        PanelComponent: SuggestNodesPanel
      },
      {
        direction: 'upstream' as const,
        aiAssistantSlug: 'suggest-bets',
        buttonText: 'Suggest Bets',
        beta: false,
        PanelComponent: SuggestNodesPanel
      }
    ]

    if (isAdmin) {
      array.push({
        direction: 'upstream' as const,
        aiAssistantSlug: 'find-related-work',
        buttonText: 'Find related work',
        beta: true,
        PanelComponent: SuggestNodesPanel
      })
    }

    // Add "Formula breakdown" only for metrics with null sourceName
    if (domainObject?.__typename === 'Metric' && domainObject?.sourceName === null) {
      array.push({
        direction: 'upstream' as const,
        aiAssistantSlug: 'extract-metrics-from-formula',
        buttonText: 'Formula breakdown',
        beta: true,
        PanelComponent: ExtractMetricsFromFormulaPanel
      })
    }

    return array
  }, [domainObject?.__typename, domainObject?.sourceName, isAdmin])

  const handleBack = useCallback(() => {
    setActivePanel(null)
    setHasInteracted(true)
  }, [setActivePanel, setHasInteracted])

  const activeAgent = useMemo(() => agents.find((a) => a.aiAssistantSlug === activePanel), [agents, activePanel])
  const activeButtonText = activeAgent?.buttonText || ''

  return (
    <PopoverRoot
      size={'lg'}
      positioning={{ placement: position }}
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open)

        if (!e.open) {
          setActivePanel(null)
          setHasInteracted(false)
        }
      }}
    >
      <PopoverTrigger asChild>
        <IconButton
          aria-label="Add cards"
          size="xs"
          borderRadius="full"
          variant="surface"
          pos="absolute"
          top={
            position === Position.Top
              ? `-${offset}px`
              : position === Position.Bottom
                ? `calc(100% + ${offset}px)`
                : '50%'
          }
          left={
            position === Position.Left
              ? `-${offset}px`
              : position === Position.Right
                ? `calc(100% + ${offset}px)`
                : '50%'
          }
          transform="translate(-50%, -50%)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          colorPalette="purple"
        >
          {loading ? <Spinner /> : <IconMap.AiTools />}
        </IconButton>
      </PopoverTrigger>
      <PopoverContent p={0} w={activePanel ? 'lg' : null} className="popover-content-transition">
        <PopoverArrow />
        <PopoverBody p={0}>
          <Box w="100%" overflow="hidden">
            {activeAgent && activePanel ? (
              <Stack className={hasInteracted ? 'slide-in-right' : undefined} p={4}>
                <Heading size="md">{activeButtonText}</Heading>
                <activeAgent.PanelComponent
                  node={node}
                  domainObject={domainObject}
                  aiAssistantSlug={activeAgent.aiAssistantSlug}
                  handle={position}
                  direction={activeAgent.direction}
                  handleBack={handleBack}
                  setLoading={setLoading}
                  loading={loading}
                  onSuccess={() => setOpen(false)}
                />
              </Stack>
            ) : (
              <Box className={hasInteracted ? 'slide-in-left' : undefined} p={4}>
                <For each={agents}>
                  {(item) => (
                    <Button
                      key={`suggest-metrics-${item.direction}-${item.aiAssistantSlug}`}
                      onClick={() => {
                        setHasInteracted(true)
                        setActivePanel(item.aiAssistantSlug)
                      }}
                      {...aiButtonStyles}
                    >
                      {item.buttonText}
                      {item.beta && <Badge colorPalette="purple">Beta</Badge>}
                    </Button>
                  )}
                </For>
              </Box>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
      <style>{`
        .slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: right center;
        }
        .slide-in-left {
          animation: slideInLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: left center;
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .popover-content-transition {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: width, height, transform;
        }
      `}</style>
    </PopoverRoot>
  )
}

export default AiNodeMenu
